import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Drawer, Flex } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import * as Sentry from "@sentry/react";
import isEqual from "lodash/isEqual";

import { GTPALCountValue } from "@/clients/interfaces/gtpal";
import {
  createGTPALInitialValues,
  createGTPALQuestionnaireResponse,
  fetchGTPALQuestionnaire,
  fetchGTPALQuestionnaireResponse,
} from "@/clients/services/gtpal";
import { sumAbortionCounts } from "@/clients/utils/gtpal-abortions";
import { DisabledReasonButton, NO_CHANGES_REASON } from "@/components/DisabledReason";
import { TextWrapper } from "@/components/TextWrapper";
import QuestionnaireForm from "@/intake-forms/components/QuestionnaireForm";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import {
  dispatchPostpartumFromGtpalSave,
  syncPostpartumTrackerFromGtpalQuestionnaireResponse,
} from "@/pregnancy";
import { RootState, useAppDispatch } from "@/store";
import { getActiveChecklist } from "@/store/slices/checklists";
import { setGTPALValues } from "@/store/slices/gtpal";
import { selectCurrentPregnancyRecord } from "@/store/slices/pregnancy";
import { useTrigger } from "@/utils/hooks/use-trigger";
import { joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";
import { getQuestionnaireSchema, QuestionnaireFormValues } from "@/utils/questionnaires";

import CloseoutAccordion from "./PregnancyCloseout/CloseoutAccordion";

interface GTPALDrawerProps {
  opened: boolean;
  onClose: () => void;
  onCloseoutComplete?: () => void;
  onSaveComplete?: () => void;
  openChecklistCloseoutDrawer: () => void;
  refreshTrigger?: number;
}

export default function GTPALDrawer({
  opened,
  onClose,
  onCloseoutComplete,
  onSaveComplete,
  openChecklistCloseoutDrawer,
  refreshTrigger,
}: GTPALDrawerProps) {
  const medplum = useMedplum();
  const activeChecklists = useSelector(getActiveChecklist);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { clientId } = useParams() as { clientId: string };

  const activePregnancyRecord = useSelector(selectCurrentPregnancyRecord(clientId));
  const pregnancyRecords = useSelector((state: RootState) => state.pregnancy[clientId] ?? {});
  const isPregnant = !!activePregnancyRecord?.isActive;

  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [initialValues, setInitialValues] = useState<QuestionnaireFormValues>();
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse>();
  const dispatch = useAppDispatch();
  const [trigger, nextTrigger] = useTrigger();
  useEffect(() => {
    (async () => {
      if (trigger >= 0) {
        const questionnaire = await fetchGTPALQuestionnaire(medplum);
        setQuestionnaire(questionnaire);

        const questionnaireResponse = await fetchGTPALQuestionnaireResponse(
          medplum,
          questionnaire,
          clientId,
        );
        setQuestionnaireResponse(questionnaireResponse);
      }
    })();
  }, [medplum, clientId, trigger, refreshTrigger]);

  const [questionnaireGroup, schema] = useMemo(() => {
    const questionnaireGroup = questionnaire?.item?.[0];
    const schema = questionnaireGroup?.item
      ? getQuestionnaireSchema(questionnaireGroup.item)
      : null;
    return [questionnaireGroup, schema];
  }, [questionnaire]);

  const form = useForm<QuestionnaireFormValues>({
    initialValues: {} as QuestionnaireFormValues,
    validate: schema ? zodResolver(schema) : undefined,
  });

  const formSetValues = form.setValues;
  const formValues = form.values;

  useEffect(() => {
    (async () => {
      try {
        const initialValues = await createGTPALInitialValues(
          medplum,
          joinReferenceString("Patient", clientId),
          questionnaireGroup?.item ?? [],
          questionnaireResponse,
        );
        setInitialValues(initialValues);
        formSetValues(initialValues);
      } catch (error) {
        showSystemErrorNotification();
        Sentry.captureException(error);
      }
    })();
  }, [formSetValues, questionnaireGroup?.item, questionnaireResponse, clientId, medplum]);

  const hasChanges = useMemo(
    () => (initialValues ? !isEqual(initialValues, formValues) : false),
    [initialValues, formValues],
  );

  const abortionsTotal = useMemo(() => {
    const { gtpalAbortionsInduced, gtpalMiscarriages } = (formValues.gtpal ??
      {}) as Partial<GTPALFormValues>;
    return sumAbortionCounts(gtpalAbortionsInduced, gtpalMiscarriages);
  }, [formValues.gtpal]);

  useEffect(() => {
    if (!opened && initialValues) {
      formSetValues(initialValues);
    }
  }, [opened, initialValues, formSetValues]);

  const onCloseGTPALDrawer = () => {
    onClose();
    if (!isPregnant && !!activeChecklists.length) openChecklistCloseoutDrawer();
  };

  const handleSubmit = async (values: QuestionnaireFormValues) => {
    if (!schema || !questionnaire) return;
    setIsSubmitting(true);
    const parsedValues = schema.parse(values);

    const updatedQuestionnaireResponse = await createGTPALQuestionnaireResponse(
      medplum,
      questionnaire,
      clientId,
      parsedValues,
      questionnaireResponse,
    );

    const syncResult = await syncPostpartumTrackerFromGtpalQuestionnaireResponse(
      medplum,
      updatedQuestionnaireResponse,
    );

    if (
      !syncResult.ok &&
      (syncResult.reason === "update_failed" || syncResult.reason === "missing_subject")
    ) {
      toast({
        type: "error",
        title: "Failed to update postpartum",
        message:
          "The delivery date was saved to GTPAL, but the postpartum data could not be updated. Try again or contact support if this continues.",
        autoClose: false,
      });
      setIsSubmitting(false);
      return;
    }

    if (syncResult.ok) {
      dispatchPostpartumFromGtpalSave(
        dispatch,
        clientId,
        pregnancyRecords,
        updatedQuestionnaireResponse,
        syncResult.data,
      );
    }

    setQuestionnaireResponse(updatedQuestionnaireResponse);
    setIsSubmitting(false);

    setInitialValues(values);
    const {
      gtpalGravida: gravida,
      gtpalTerm: term,
      gtpalPreterm: preterm,
      gtpalLiving: living,
    } = values.gtpal as GTPALFormValues;
    dispatch(setGTPALValues({ gravida, term, preterm, abortion: abortionsTotal, living }));
    onSaveComplete?.();
    onCloseGTPALDrawer();
  };

  return (
    <Drawer.Root
      trapFocus
      size={512}
      opened={opened}
      position="right"
      closeOnClickOutside={false}
      onClose={onCloseGTPALDrawer}
    >
      <Drawer.Overlay />
      <Drawer.Content data-testid="gtpal-drawer">
        <form onSubmit={form.onSubmit((values, _) => handleSubmit(values))}>
          <Drawer.Header h={64} px="md" bg="gray.0">
            <Flex w="100%" gap="md" align="center" justify="space-between">
              <Flex gap="md" align="center" style={{ flex: 1 }}>
                <Drawer.CloseButton data-testid="gtpal-drawer-close-button" />
                <Drawer.Title style={{ flex: 1 }}>
                  <TextWrapper fz="lg" variant="bold" style={{ wordBreak: "break-word" }}>
                    GTPAL
                  </TextWrapper>
                </Drawer.Title>
              </Flex>
              <DisabledReasonButton
                type="submit"
                miw="fit-content"
                loading={isSubmitting}
                disabledReason={hasChanges ? undefined : NO_CHANGES_REASON}
              >
                Save
              </DisabledReasonButton>
            </Flex>
          </Drawer.Header>
          <Drawer.Body p="lg">
            <Flex gap="lg" direction="column">
              <PermissionGate mode="hide" requiredPermissions={PROVIDER_SCRIBE_PERMISSION_FLAGS}>
                <CloseoutAccordion
                  closeGTPALDrawer={onClose}
                  trigger={() => {
                    nextTrigger();
                    onCloseoutComplete?.();
                  }}
                />
              </PermissionGate>
              <QuestionnaireForm
                form={form}
                group={questionnaireGroup}
                questionnaire={questionnaire}
              />
              <TextWrapper size="sm" c="gray.7" data-testid="gtpal-abortions-total">
                Abortions (A) total: {abortionsTotal}
              </TextWrapper>
            </Flex>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}

interface GTPALFormValues {
  gtpalGravida: number | undefined;
  gtpalTerm: number | undefined;
  gtpalPreterm: number | undefined;
  gtpalAbortionsInduced: GTPALCountValue;
  gtpalMiscarriages: GTPALCountValue;
  gtpalLiving: number | undefined;
}
